import React, {useEffect, useState} from 'react';
import Routing from '../../Routing.ts';
import FiberOfflineFlow from "../../components/selfcare/ErrorReport/Flows/FiberOfflineFlow.tsx";
import OtherUnstableFlow from "../../components/selfcare/ErrorReport/Flows/OtherUnstableFlow.tsx";
import CoaxOfflineFlow from "../../components/selfcare/ErrorReport/Flows/CoaxOfflineFlow.tsx";
import OtherSpeedFlow from "../../components/selfcare/ErrorReport/Flows/OtherSpeedFlow.tsx";
import OtherOfflineFlow from "../../components/selfcare/ErrorReport/Flows/OtherOfflineFlow.tsx";
import {useCustomer} from "../../layouts/SelfcareLayout.tsx";
import {
    ERROR_REPORT_STATUS,
    FlowData,
    ProductInstance
} from "../../util/types.ts";
import {selfcareGetErrorReportStatus, selfcareReportError} from "../../util/request.ts";
import SelfcarePageContent from "../../components/selfcare/SelfcarePageContent.tsx";
import Icon from "../../components/selfcare/Icon.tsx";
import IconCheck from "../../components/icons/IconCheck.tsx";
import {Link, useNavigate} from "react-router-dom";
import FormFieldsetComponent from "../../components/selfcare/ErrorReport/FormElements/FormFieldsetComponent.tsx";
import useEvent from "react-use-event";
import {ResetErrorReportPageEvent} from "../../util/events.ts";
import {Tech, Options } from "../../util/SelfcareErrorReportTypes.ts";
import CustomerProductSelectionComponent from '../../components/selfcare/ErrorReport/FormElements/CustomerProductSelectionComponent.tsx';
import CheckLocalDisruptionsComponent from "../../components/selfcare/ErrorReport/FormElements/CheckLocalDisruptionsComponent.tsx";
import InternetIssueOptionsComponent from '../../components/selfcare/ErrorReport/FormElements/InternetIssueOptionsComponent.tsx';
import ReportErrorHeaderComponent from '../../components/selfcare/ErrorReport/FormElements/ReportErrorHeaderComponent.tsx';
import ScrollToTop from "../../components/ScrollToTop.tsx";

const optionMap: Options = {
    'offline': {
        display_name: 'Mit internet virker slet ikke',
        name: 'offline',
        forms: {
            'Fiber': FiberOfflineFlow,
            'Coax': CoaxOfflineFlow,
            'Other': OtherOfflineFlow,
        }
    },
    'unstable': {
        display_name: 'Der er udfald på mit internet',
        name: 'unstable',
        forms: {
            'Fiber': OtherUnstableFlow,
            'Coax': OtherUnstableFlow,
            'Other': OtherUnstableFlow,
        }
    },
    'speed': {
        display_name: 'Min hastighed lever ikke op til mine forventninger',
        name: 'speed',
        forms: {
            'Fiber': OtherSpeedFlow,
            'Coax': OtherSpeedFlow,
            'Other': OtherSpeedFlow,
        }
    },
};

const validOption = (option_name: string): boolean => {
    return option_name in optionMap
}

export default function ReportErrorPage() {
    ScrollToTop()

    const {customer} = useCustomer();
    const [step, setStep] = useState<number>(1);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [reason, setReason] = useState<string>('');
    const [tech, setTech] = useState<string>('');
    const [product, setProduct] = useState<ProductInstance | null>(null);
    const navigate = useNavigate();
    const [ticketId, setTicketId] = useState<number>();
    const [rateLimitedProductIds, setRateLimitedProductIds] = useState<number[]>([]);

    const handleContinue = () => {
        setStep(2);
        scrollTo(0, 0);
    };

    useEffect(() => {
        selfcareGetErrorReportStatus(customer.customer_id).then(response => {
            const status = response.response.data.error_report_status;
            if (status !== ERROR_REPORT_STATUS.ACTIVE) {
                navigate(Routing.Selfcare.Internet);
            }
            setRateLimitedProductIds(response.response.data.rate_limited_product_instance_id);
        });
    }, [customer.customer_id, navigate]);

    useEvent<ResetErrorReportPageEvent>('ResetErrorReportPageEvent', () => {
        setStep(1)
        scrollTo(0, 0);
    })

    const submitFunction = async (customer_id: string, product_instance_id: number, flowData: FlowData) => {
        const response = await selfcareReportError(customer_id, product_instance_id, flowData)
        switch (response.code) {
            case 200: {
                const ticket_id = response.response.data.ticket_id;
                if (ticket_id === null) {
                    return 'Fejlmelding kunne ikke oprettes. Prøv venligst igen senere, eller kontakt vores kundeservice direkte';
                }
                setStep(3);
                scrollTo(0, 0);
                setTicketId(ticket_id);
                return '';
            }

            case 429:
                return 'Du har fornyligt sendt en fejlmelding ind. Lad os venligst behandle din sag inden du opretter en ny. Tak for din tålmodighed.';

            default:
                return 'Noget gik galt. Prøv venligst igen senere, eller kontakt vores kundeservice direkte';
        }
    }

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked);
    };

    return (
        <SelfcarePageContent className={'selfcare-page-report-error'}>
            <ReportErrorHeaderComponent/>

            {step === 1 && (
                <CheckLocalDisruptionsComponent
                    isChecked={isChecked}
                    handleCheckboxChange={handleCheckboxChange}
                    handleContinue={handleContinue}
                />
            )}
            {step === 2 && (
                <div className={'selfcare-report-error-form'}>
                    <FormFieldsetComponent
                        number={1}
                        title={'Vælg type'}
                        className={'report-reason'}
                    >
                        <CustomerProductSelectionComponent
                            customer={customer}
                            tech={tech}
                            setTech={setTech}
                            onProductSelect={setProduct}
                            rateLimitedProductIds={rateLimitedProductIds}
                        />
                        {tech && product && (
                            <InternetIssueOptionsComponent onChange={setReason} options={optionMap}/>
                        )}
                    </FormFieldsetComponent>
                    {tech && product && validOption(reason) && React.createElement(optionMap[reason].forms[tech as Tech], {product, submitFunction})}
                </div>
            )}
            {step === 3 && (
                <div className={'selfcare-report-error-success'}>
                    <h2 className={'heading heading--montserrat'}>
                    <Icon component={IconCheck}/>
                        <span className="text">

                        Vi har modtaget din fejlmelding
                        </span>
                    </h2>
                    <p>
                        Din fejlmelding har ID: <strong>{ticketId}</strong>
                    </p>
                    <p>
                        Du modtager hurtigst muligt en kvitteringsmail.
                        Har du spørgsmål eller tilføjelser til din fejlmelding, er du velkommen til at svare tilbage på mailen.
                    </p>
                    <p className={'align-center'}>
                        <Link
                            className={'button button--montserrat teal rounded'}
                            to={Routing.Selfcare.Internet}
                        >
                            Tilbage til Internet-siden
                        </Link>
                    </p>
                </div>
            )}
        </SelfcarePageContent>
    );
}

